import * as React from "react";
import * as calendarTaskService from "../../models/services/calendarTaskService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { IEndlessList } from "ui/Scripts/source/models/common/endlessList";
import { ListLine } from "../framework/listLine";
import { ConfirmationDialogResult, UserGroupType } from "ui/Scripts/source/models/enums";
import { AvatarImage } from "ui/Scripts/source/components/framework/avatarImage";
import { AppUtils } from "../../framework/appUtils";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IUserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { SelectUserGroupDialog } from "../framework/selectUserGroupDialog";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";

//CalendarTaskHandlerListMenu
export interface ICalendarTaskHandlerListMenuProp {
    onRemove: () => void;
    onCancel: () => void;
}

export class CalendarTaskHandlerListMenu extends React.Component<ICalendarTaskHandlerListMenuProp, {}> {
    handleRemoveClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onRemove();
    };

    handleCancelClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onCancel();
    };
        
    render() {
        return (
            <div className="listPopupMenu" onClick={this.handleCancelClick}>
                <div className="menu">
                    <div className="line" onClick={this.handleRemoveClick}>{Translations.Remove}</div>
                    <div className="line" onClick={this.handleCancelClick}>{Translations.Cancel}</div>
                </div>
            </div>
        );
    }
}

//CalendarTaskHandlerListTitleRow
//***************************************************************
export class CalendarTaskHandlerListTitleRow extends React.Component<{}, {}> {
    render() {
        return (
            <div className="fullLeft lineContainer">
                <div className="line title">
                    <div className="row">
                        <div className="col-12">{Translations.Name}</div>
                    </div>
                </div>
            </div>
        );
    }
}

//CalendarTaskHandlerListLine
//***************************************************************
export interface ICalendarTaskHandlerListLineProp {
    handler: IModifyUser;
    selectedCode: string;
    onClick: (code: string) => void;
    onLongPress: (code: string) => void;
}

export class CalendarTaskHandlerListLine extends React.Component<ICalendarTaskHandlerListLineProp, {}> {
    render() {
        const props = this.props;
        const handler = props.handler;
        return (
            <ListLine
                id={props.handler.code}
                first={false}
                selected={props.selectedCode === props.handler.code}
                classes="line"
                onClick={props.onClick}
                onLongPress={props.onLongPress}
                onRefreshList={null}>
                <div className="row">
                    <div className="col-12">
                        <AvatarImage
                            classes="leftDataCont"
                            name={handler.name}
                            image={handler.picture}
                        />
                        <div className="dataCont rightAvatar">
                            <div className="singleLine">{handler.name}</div>
                        </div>
                    </div>
                </div>
            </ListLine>
        );
    }
}

//CalendarTaskHandlerList
//***************************************************************
export interface ICalendarTaskHandlerListProp {
    isReadOnly: boolean;
    handlers: IModifyUser[];
    noHandlersMessage?: string;
    removeUsersConfirmation?: string;
    onRemoveHandler: (code: string) => Promise<ApiSuccess>;
    onAddHandlers: (userGroups: IModifyUser[]) => Promise<ApiSuccess>;
}

interface ICalendarTaskHandlerListState {
    selectedCode: string;
    showListMenu: boolean;
    showSelectUserGroupDialog: boolean;
}

export class CalendarTaskHandlerList extends React.Component<ICalendarTaskHandlerListProp, ICalendarTaskHandlerListState> {
    constructor(props) {
        super(props);
        this.state = { selectedCode: null, showListMenu: false, showSelectUserGroupDialog: false };
    }
    
    handleLineClick = (selectedCode: string) => {
        this.setState({
            selectedCode
        });
    };

    //#region Adding
    handleAddClick = () => {
        this.setState({ showSelectUserGroupDialog: true });
    };

    handleLoadUserGroups = (page: number, groupType: UserGroupType): Promise<IEndlessList<IUserGroupListItem>> => {
        return AppUtils.callService(() => calendarTaskService.getUserGroupListItems(page, null, "name", true, groupType, this.props.handlers.map(i => i.code), null));
    };

    handleSelectUserGroupDialogOk = async (userGroups: IUserGroupListItem[], userGroupType: UserGroupType) => {
        const props = this.props;
        if (!userGroups || userGroups.length < 1) return;
        const newUserGroups = userGroups.map(user => {
            const newUser = new ModifyUser();
            newUser.code = user.code;
            newUser.name = user.name;
            newUser.picture = user.picture;
            newUser.date = userGroupType;
            return newUser;
        });
        // Call server
        const result = await AppUtils.callService(() => props.onAddHandlers(newUserGroups));
        if (!result) return;
        this.setState({
            showSelectUserGroupDialog: false
        });
    };
    
    handleSelectUserGroupDialogCancel = () => {
        this.setState({
            showSelectUserGroupDialog: false
        });
    };
    //#endregion

    handleLineLongPress = (selectedCode: string) => {
        this.setState({
            selectedCode,
            showListMenu: true
        });
    };

    handleMenuRemove = async () => {
        const selectedCode = this.state.selectedCode;
        if (!selectedCode) return;
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(props.removeUsersConfirmation ?? Translations.DoYouReallyWantToRemoveSelectedUsers, false) === ConfirmationDialogResult.Yes) {
            // Call server
            await AppUtils.callService(() => props.onRemoveHandler(selectedCode));
        }
        this.setState({
            showListMenu: false
        });
    };

    handleMenuCancel = () => {
        this.setState({
            showListMenu: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const handlers = props.handlers;
        return (
            <div>
                <div className="fullLeft">
                    <ToolButton
                        tooltip={Translations.Add}
                        disabled={props.isReadOnly}
                        classes={"round right mono add"}
                        onClick={this.handleAddClick}/>
                </div>
                {!props.isReadOnly && handlers.length === 0 &&
                    <div className="fullLeft">
                        <div className="alert alert-info" role="alert">
                            {Translations.AddEventHandlersToEvent}
                        </div>
                    </div>
                }
                <CalendarTaskHandlerListTitleRow />
                <div className="fullLeft lineContainer eventHandlers">
                    {(props.noHandlersMessage && handlers?.length === 0) && (
                        <div className="alert alert-info" role="alert">
                            {props.noHandlersMessage}
                        </div>
                    )}
                    {handlers.map((handler) =>
                        <CalendarTaskHandlerListLine
                            key={handler.code}
                            selectedCode={state.selectedCode}
                            handler={handler}
                            onClick={this.handleLineClick}
                            onLongPress={!props.isReadOnly ? this.handleLineLongPress : null}
                        />
                    )}
                </div>
                {state.showListMenu &&
                    <CalendarTaskHandlerListMenu
                        onRemove={this.handleMenuRemove}
                        onCancel={this.handleMenuCancel}
                    />
                }
                {state.showSelectUserGroupDialog &&
                    <SelectUserGroupDialog
                        title={Translations.SelectCalendarTaskHandlers}
                        loadUserGroups={this.handleLoadUserGroups}
                        multiSelect={true}
                        onOk={this.handleSelectUserGroupDialogOk}
                        onCancel={this.handleSelectUserGroupDialogCancel}
                    />
                }
            </div>
        );
    }
}